import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useSendUserOperation } from '@coinbase/cdp-hooks';
import { Button } from '@coinbase/cdp-react/components/ui/Button';

type Props = {
  destinationAddress?: `0x${string}`;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onError?: (error: Error) => void;
  onSuccess?: (hash: string) => void;
  sourceAddress?: `0x${string}`;
};

function SendEvmSmartTransaction({
  destinationAddress,
  onClick,
  onError,
  onSuccess,
  sourceAddress,
}: Props) {
  const {
    sendUserOperation,
    data: { transactionHash } = {},
    status,
    error,
  } = useSendUserOperation();
  const [isUserOpPending, setIsUserOpPending] = useState(false);

  const isPending = isUserOpPending || status === 'pending';

  const handleSendUserOperation = useCallback(async () => {
    if (!destinationAddress || !sourceAddress) return;

    try {
      setIsUserOpPending(true);

      const result = await sendUserOperation({
        evmSmartAccount: sourceAddress,
        network: 'base-sepolia',
        calls: [
          {
            to: destinationAddress, // Send to yourself for testing
            value: 1000000000000n, // 0.000001 ETH in wei
            data: '0x', // Empty data for simple transfer
          },
        ],
      });

      if (!result.userOperationHash) {
        throw new Error('Failed to send user operation');
      }

      // let the useEffect hook handle setting isUserOpPending to false on success
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Unknown error occurred'));
      setIsUserOpPending(false);
    }
  }, [destinationAddress, onError, sendUserOperation, sourceAddress]);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      onClick?.(e);
      void handleSendUserOperation();
    },
    [handleSendUserOperation, onClick],
  );

  useEffect(() => {
    if (status === 'pending') {
      setIsUserOpPending(false);
      return;
    }
    if (status === 'success' && transactionHash) {
      onSuccess?.(transactionHash);
      return;
    }
    if (status === 'error') {
      onError?.(error ?? new Error('Unknown error occurred'));
    }
  }, [error, status, onError, onSuccess, transactionHash]);

  return (
    <Button onClick={handleClick} isPending={isPending}>
      Send transaction
    </Button>
  );
}

export { SendEvmSmartTransaction };
